import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import api from 'utils/axios';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, UserPlus as PersonAddIcon } from 'lucide-react';
import PatientFormDialog from '../PatientFormDialog';

const FileUpload = ({ onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [newPatientDialogOpen, setNewPatientDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load patients
  const loadPatients = useCallback(async () => {
    try {
      const response = await api.get('/patients/');
      console.log('Patients API response:', response.data);
      // PatientViewSet returns {status: "success", count: X, data: [...]} when not paginated
      // or {results: [...]} when paginated
      const patientsData = response.data.data || response.data.results || response.data;
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatients([]); // Ensure patients is always an array
    }
  }, []);

  // Load patients on mount
  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Handle patient creation success
  const handlePatientCreated = (createdPatient) => {
    setSuccess('Patient created successfully!');
    // Refresh patients list and set the new patient as selected
    loadPatients();
    setSelectedPatient(createdPatient.patient_id);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!selectedPatient) {
      setError('Please select a patient before uploading');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('patient', selectedPatient);
    formData.append('original_filename', file.name);
    console.log(file.name);
    try {
      console.log(formData)
      const response = await api.post('/documents/uploads/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
      });

      setSuccess(true);
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [onUploadSuccess, selectedPatient]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Patient Selection */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <InputLabel>Select Patient *</InputLabel>
              <Select
                value={selectedPatient}
                label="Select Patient *"
                onChange={(e) => setSelectedPatient(e.target.value)}
              >
                {patients.map((patient) => (
                  <MenuItem key={patient.patient_id} value={patient.patient_id}>
                    {patient.first_name} {patient.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon size={16} />}
              onClick={() => setNewPatientDialogOpen(true)}
              fullWidth
            >
              New Patient
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          opacity: selectedPatient ? 1 : 0.6,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon size={48} />
        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? 'Drop the PDF file here'
            : 'Drag and drop a PDF file here, or click to select'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Only PDF files are accepted
        </Typography>
        {!selectedPatient && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Please select a patient first
          </Typography>
        )}
      </Box>

      {uploading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          File uploaded successfully! Processing has started.
        </Alert>
      )}

      {/* New Patient Dialog */}
      <PatientFormDialog
        open={newPatientDialogOpen}
        onClose={() => setNewPatientDialogOpen(false)}
        onPatientCreated={handlePatientCreated}
        loading={loading}
        onLoadingChange={setLoading}
      />
    </Box>
  );
};

export default FileUpload;
